/**
 * GLW — footer menu disclosure
 * assets/glw-footer-menu.js
 *
 * Footer link lists collapse into a native <details>/<summary> disclosure
 * below 990px and become static, permanently expanded navigation above it.
 *
 * Why this needs JavaScript at all:
 * CSS cannot express "interactive disclosure at one size, static content at
 * another". Suppressing the summary with pointer-events leaves it focusable
 * and still announcing as an expandable control when nothing can collapse —
 * worse than not trying. So the open state is synced here instead, and the
 * heading has two presentations in the markup with display:none keeping
 * exactly one in the accessibility tree.
 *
 * No-JS behaviour: the markup ships `open`, so menus render expanded. Longer
 * than intended on mobile, but every link is reachable. The inverse — shipping
 * closed and relying on JS to open — would hide navigation on failure.
 *
 * Breakpoint is 990 to match assets/section-glw-footer.css. Note the theme has
 * an open question about whether 750/990 or production's values are correct
 * (see THEME-CHANGES.md debt 3); if that is resolved, change both together.
 */

if (!customElements.get('footer-menu')) {
  customElements.define(
    'footer-menu',
    class FooterMenu extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        if (!this.details) return;

        this.breakpoint = window.matchMedia('(max-width: 989px)');

        // Mobile starts collapsed, matching production. The user's last mobile
        // state is remembered across a resize rather than reset, which costs
        // nothing and avoids surprising them.
        this.mobileOpen = false;

        this.onToggle = this.onToggle.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.details.addEventListener('toggle', this.onToggle);
        this.breakpoint.addEventListener('change', this.onBreakpointChange);

        this.update(this.breakpoint.matches);
      }

      disconnectedCallback() {
        this.details?.removeEventListener('toggle', this.onToggle);
        this.breakpoint?.removeEventListener('change', this.onBreakpointChange);
      }

      onToggle() {
        if (this.breakpoint.matches) {
          this.mobileOpen = this.details.open;
        }
      }

      onBreakpointChange(event) {
        this.update(event.matches);
      }

      update(isMobile) {
        if (isMobile) {
          this.details.open = this.mobileOpen;
          return;
        }
        this.details.open = true;
      }
    }
  );
}
